<template>
  <div class="create-tournament">
    <!-- Tournament Creation Button -->
    <b-button
      :type="buttonType"
      :outlined="outlined"
      :icon-left="buttonIcon"
      :loading="isCreating"
      @click="openTournamentForm"
    >
      {{ buttonText }}
    </b-button>

    <!-- Tournament Creation Modal -->
    <b-modal
      v-model="isModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
    >
      <template>
        <div class="modal-card" style="position: relative">
          <b-loading :is-full-page="false" v-model="isCreating"></b-loading>
          <ClassTournamentForm
            :tournament="defaultTournamentData"
            @onCreate="handleTournamentCreate"
            @close="closeModal"
          />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import ClassTournamentForm from "@/components/admins/tournaments/offline/ClassTournamentForm.vue";
import ClassTournamentService from "@/services/ClassTournamentService";
import { LectureService } from "@/services/CoachingServices";

export default {
  name: "CreateOfflineClassTournament",

  components: {
    ClassTournamentForm,
  },

  props: {
    currentClass: {
      type: Object,
      required: true,
    },
    lecture: {
      type: Object,
      default: null,
    },
    buttonText: {
      type: String,
      default: "Tournament",
    },
    buttonType: {
      type: String,
      default: "is-primary is-light",
    },
    buttonIcon: {
      type: String,
      default: "chess",
    },
    outlined: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isCreating: false,
      isModalActive: false,
    };
  },

  computed: {
    /**
     * Generate default tournament data based on context
     */
    defaultTournamentData() {
      const baseData = {
        startTime: new Date(),
        baseTimeControl: 15,
        increment: 0,
        type: "Swiss",
        totalRounds: 5,
        doubleRR: false,
        groupSize: 0,
        initialFen: "",
      };

      // If lecture is provided, generate lecture-specific name
      const className =
        this.currentClass.schoolName + " | " + this.currentClass.groupName ||
        "Class";
      const classDate = (
        this.lecture?.lectureDate || new Date()
      ).toLocaleDateString();
      baseData.name = `${className} - ${classDate}`;

      return baseData;
    },

    /**
     * Determine if this is a lecture-based tournament
     */
    isLectureTournament() {
      return !!this.lecture;
    },

    /**
     * Get participating students based on context
     * For lecture tournaments: filter by attendance
     * For regular tournaments: all class students
     */
    participatingStudents() {
      if (!this.currentClass?.students) {
        return [];
      }

      // If this is a lecture tournament, filter by attendance
      // lecture.attendanceRecords is currently empty, so use SAC data in this.currentClass.students for now
      if (this.isLectureTournament) {
        return this.currentClass.students.filter((record) => record.attend);
      }

      // For regular tournaments, return all students
      return this.currentClass.students;
    },
  },

  methods: {
    /**
     * Open the tournament creation form
     */
    openTournamentForm() {
      if (!this.currentClass) {
        this.$buefy.toast.open({
          message: "No class selected. Please select a class first.",
          type: "is-warning",
        });
        return;
      }

      this.isModalActive = true;
    },

    /**
     * Close the modal and reset loading state
     */
    closeModal() {
      if (!this.isCreating) {
        this.isModalActive = false;
      }
    },

    /**
     * Handle tournament creation
     */
    async handleTournamentCreate(tournamentData) {
      this.isCreating = true;

      try {
        // Create tournament with async player addition
        const tournamentId = await ClassTournamentService.createTournament({
          tournamentData,
          currentClass: this.currentClass,
        });

        // If this is a lecture tournament, save tournamentId to lecture
        if (this.isLectureTournament) {
          await this.saveTournamentToLecture(tournamentId);
        }

        // Get participating student IDs for lecture tournaments
        const participatingStudentIds = this.isLectureTournament
          ? this.participatingStudents.map((student) => student.studentId)
          : null;

        // Add players via Firebase function but don't wait for the return
        console.log("Adding players to tournament", {
          tournamentId,
          currentClass: this.currentClass,
          participatingStudentIds,
        });
        await ClassTournamentService.addPlayersToTournament({
          tournamentId,
          currentClass: this.currentClass,
          participatingStudentIds,
        }).catch((error) => {
          console.error("Error adding players to tournament:", error);
        });

        // Close modal and show success immediately
        this.isModalActive = false;
        this.isCreating = false;

        this.$buefy.toast.open({
          message:
            "Tournament created successfully! Students are being added in the background.",
          type: "is-success",
          duration: 4000,
        });

        // Emit success event with tournament ID
        this.$emit("tournament-created", tournamentId);

        // Redirect to tournament page immediately
        this.$router.push(`/teacher/class-tournaments/${tournamentId}`);
      } catch (error) {
        console.error("Error creating tournament:", error);
        this.$buefy.toast.open({
          message: `Failed to create tournament: ${error.message}`,
          type: "is-danger",
          duration: 5000,
        });

        // Emit error event
        this.$emit("tournament-error", error);
        this.isCreating = false;
      }
    },

    /**
     * Save tournament ID to lecture document
     */
    async saveTournamentToLecture(tournamentId) {
      if (!this.lecture) return;

      await LectureService.setTournamentId(this.lecture.id, tournamentId);
    },
  },
};
</script>

<style scoped>
.create-tournament {
  display: inline-block;
}
</style>
